import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { toast } from "sonner";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  type: 'Buy',
  asset_class_id: '',
  symbol: '',
  quantity: '',
  price: '',
  brokerage: '0',
  taxes: '0',
  other_charges: '0',
  date: format(new Date(), 'yyyy-MM-dd'),
  notes: ''
};

function InvestmentTransactionDialog({ open, onOpenChange, assetClasses, currency, holdings, onSaved }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({ ...EMPTY_FORM, date: format(new Date(), 'yyyy-MM-dd') });
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.asset_class_id || !formData.symbol || !formData.quantity || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }
    const qty = parseFloat(formData.quantity);
    const price = parseFloat(formData.price);
    if (!(qty > 0) || !(price > 0)) {
      toast.error('Quantity and price must be greater than 0');
      return;
    }
    // Client-side oversell check
    if (formData.type === 'Sell') {
      const sym = formData.symbol.trim().toUpperCase();
      const h = holdings.find(x => x.symbol === sym && x.asset_class_id === formData.asset_class_id);
      const avail = h?.total_quantity || 0;
      if (qty > avail) {
        toast.error(
          avail > 0
            ? `Cannot sell ${qty} of ${sym} — only ${avail} available`
            : `No existing holding for ${sym}`
        );
        return;
      }
    }

    setLoading(true);
    try {
      const body = {
        date: formData.date,
        asset_class_id: formData.asset_class_id,
        symbol: formData.symbol.trim().toUpperCase(),
        type: formData.type,
        quantity: qty,
        price: price,
        brokerage: parseFloat(formData.brokerage) || 0,
        taxes: parseFloat(formData.taxes) || 0,
        other_charges: parseFloat(formData.other_charges) || 0,
        currency: currency,
        notes: formData.notes || null
      };

      const res = await fetch(`${API}/investments/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (res.ok) {
        toast.success(`${formData.type} order recorded`);
        onSaved();
      } else {
        // Best-effort body read with status-based fallback (preview env can pre-consume body)
        let detail = null;
        try {
          const text = await res.text();
          detail = JSON.parse(text).detail;
        } catch {
          // body unavailable - use status-based fallback
        }
        let msg;
        if (typeof detail === 'string') {
          msg = detail;
        } else if (Array.isArray(detail) && detail[0]?.msg) {
          msg = detail[0].msg;
        } else if (res.status === 400) {
          msg = 'Invalid transaction — please review';
        } else if (res.status === 422) {
          msg = 'Validation failed — check your inputs';
        } else {
          msg = `Failed to save transaction (HTTP ${res.status})`;
        }
        toast.error(msg);
      }
    } catch {
      toast.error('Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  // Compute live total preview
  const qty = parseFloat(formData.quantity) || 0;
  const price = parseFloat(formData.price) || 0;
  const brokerage = parseFloat(formData.brokerage) || 0;
  const taxes = parseFloat(formData.taxes) || 0;
  const other = parseFloat(formData.other_charges) || 0;
  const charges = brokerage + taxes + other;
  const total = formData.type === 'Buy' ? qty * price + charges : qty * price - charges;

  // For sell, show available qty
  const matchingHolding = holdings.find(
    h => h.symbol === formData.symbol.trim().toUpperCase() &&
         h.asset_class_id === formData.asset_class_id
  );
  const available = matchingHolding?.total_quantity || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
            Investment Transaction
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Record a Buy or Sell. Holdings update automatically.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <ToggleGroup
              type="single"
              value={formData.type}
              onValueChange={(v) => v && setFormData({ ...formData, type: v })}
              className="grid grid-cols-2 gap-2"
            >
              <ToggleGroupItem
                data-testid="toggle-buy"
                value="Buy"
                className="data-[state=on]:bg-success data-[state=on]:text-success-foreground border border-border"
              >
                Buy
              </ToggleGroupItem>
              <ToggleGroupItem
                data-testid="toggle-sell"
                value="Sell"
                className="data-[state=on]:bg-destructive data-[state=on]:text-destructive-foreground border border-border"
              >
                Sell
              </ToggleGroupItem>
            </ToggleGroup>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inv-asset-class">Asset Class *</Label>
                <Select
                  value={formData.asset_class_id}
                  onValueChange={(v) => setFormData({ ...formData, asset_class_id: v })}
                >
                  <SelectTrigger data-testid="investment-asset-class-select" id="inv-asset-class">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {assetClasses.map((a) => (
                      <SelectItem key={a.asset_class_id} value={a.asset_class_id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="inv-symbol">Symbol / Ticker *</Label>
                <Input
                  data-testid="investment-symbol-input"
                  id="inv-symbol"
                  placeholder="e.g. AAPL, BTC, RELIANCE"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                  required
                />
                {formData.type === 'Sell' && available > 0 && (
                  <p className="text-xs text-muted-foreground">Available: {available}</p>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inv-qty">Quantity *</Label>
                <Input
                  data-testid="investment-quantity-input"
                  id="inv-qty"
                  type="number"
                  step="any"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-price">{formData.type === 'Buy' ? 'Buy' : 'Sell'} Price *</Label>
                <Input
                  data-testid="investment-price-input"
                  id="inv-price"
                  type="number"
                  step="any"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="inv-brk">Brokerage</Label>
                <Input
                  data-testid="investment-brokerage-input"
                  id="inv-brk"
                  type="number"
                  step="any"
                  min="0"
                  value={formData.brokerage}
                  onChange={(e) => setFormData({ ...formData, brokerage: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-tax">Taxes</Label>
                <Input
                  data-testid="investment-taxes-input"
                  id="inv-tax"
                  type="number"
                  step="any"
                  min="0"
                  value={formData.taxes}
                  onChange={(e) => setFormData({ ...formData, taxes: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-oth">Other</Label>
                <Input
                  data-testid="investment-other-input"
                  id="inv-oth"
                  type="number"
                  step="any"
                  min="0"
                  value={formData.other_charges}
                  onChange={(e) => setFormData({ ...formData, other_charges: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inv-date">Date *</Label>
              <Input
                data-testid="investment-date-input"
                id="inv-date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inv-notes">Notes (Optional)</Label>
              <Textarea
                data-testid="investment-notes-input"
                id="inv-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            <div className="p-3 bg-secondary/50 rounded-md border border-border">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Total {formData.type === 'Buy' ? 'Cost' : 'Proceeds'}
                </p>
                <p className="text-lg tracking-tight font-bold">
                  {currency} {total.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              data-testid="save-investment-button"
              type="submit"
              disabled={loading}
              className="transition-transform duration-200 hover:-translate-y-px"
            >
              {loading ? 'Saving...' : `Record ${formData.type}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default InvestmentTransactionDialog;
